
/*  src/components/LineCalibrationCanvas.js
    Universal line / area calibration canvas
    – now supports: people (4 pts), queue (4 pts), idle_area (4 pts) **and vehicle (2 pts)**
*/

import React, {
  useRef, useState, useEffect, useCallback,
} from "react";
import PropTypes from "prop-types";
import { saveLineCalibration } from "../Api";
import "../App.css";

/* ────────────── constants ────────────────────────────────────────── */
const POINT_RADIUS = 6;
const BASE_HIT_RADIUS = 12; // Base radius for hit detection
const BASE_HOVER_RADIUS = 15; // Base radius for hover detection

// Adaptive edge detection thresholds
const getEdgeThresholds = (canvasSize) => {
  if (!canvasSize.width || !canvasSize.height) {
    return { x: 1, y: 1 }; // Fallback to 1 pixel
  }
  
  // Calculate percentage-based thresholds with minimum pixel guarantees
  const percentageThreshold = 0.005; // 0.5% of canvas dimension
  const minPixelThreshold = 1; // Minimum 1 pixel
  const maxPixelThreshold = 3; // Maximum 3 pixels for very large canvases
  
  const xThreshold = Math.max(
    minPixelThreshold,
    Math.min(maxPixelThreshold, Math.ceil(canvasSize.width * percentageThreshold))
  );
  
  const yThreshold = Math.max(
    minPixelThreshold,
    Math.min(maxPixelThreshold, Math.ceil(canvasSize.height * percentageThreshold))
  );
  
  return { x: xThreshold, y: yThreshold };
};

// Universal coordinate conversion system for consistency across camera types
const normalizeCoordinate = (pixelCoord, canvasDimension) => {
  if (!canvasDimension || canvasDimension <= 0) return 0;
  return Math.max(0, Math.min(1, pixelCoord / canvasDimension));
};

const denormalizeCoordinate = (normalizedCoord, canvasDimension) => {
  if (!canvasDimension || canvasDimension <= 0) return 0;
  return Math.max(0, Math.min(canvasDimension, normalizedCoord * canvasDimension));
};

// Camera resolution classification for adaptive scaling
const getResolutionClass = (width, height) => {
  const area = width * height;
  const aspectRatio = Math.max(width, height) / Math.min(width, height);
  
  let resClass;
  if (area <= 480 * 360) resClass = 'SD';
  else if (area <= 1280 * 720) resClass = 'HD';
  else if (area <= 1920 * 1080) resClass = 'FHD';
  else if (area <= 2560 * 1440) resClass = '2K';
  else resClass = '4K+';
  
  return {
    class: resClass,
    area,
    aspectRatio,
    isUltrawide: aspectRatio > 2.1,
    isPortrait: height > width,
    density: Math.sqrt(area) // Linear dimension representation
  };
};

// Robust dynamic radius calculation for all camera types
const getDynamicRadius = (canvasSize, baseRadius, isEdgePoint = false) => {
  if (!canvasSize.width || !canvasSize.height) {
    console.warn('Invalid canvas size for radius calculation, using base radius');
    return Math.max(baseRadius, 8); // Fallback minimum
  }
  
  // Get camera resolution characteristics
  const resInfo = getResolutionClass(canvasSize.width, canvasSize.height);
  
  // Adaptive scaling based on resolution class instead of fixed standard
  const scaleFactors = {
    'SD': 0.7,     // Smaller cameras need relatively larger hit areas
    'HD': 1.0,     // Baseline
    'FHD': 1.2,    // Slightly larger for high-res
    '2K': 1.4,     // Larger for very high-res
    '4K+': 1.6     // Largest for ultra high-res
  };
  
  let scaleFactor = scaleFactors[resInfo.class] || 1.0;
  
  // Aspect ratio compensation - wider screens need adjusted hit areas
  if (resInfo.isUltrawide) {
    scaleFactor *= 1.2; // 20% larger for ultrawide cameras
  } else if (resInfo.aspectRatio > 1.5) {
    scaleFactor *= 1.1; // 10% larger for wide cameras
  }
  
  // Portrait orientation adjustment
  if (resInfo.isPortrait) {
    scaleFactor *= 1.15; // Slightly larger for portrait cameras
  }
  
  // Touch device detection
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const touchMultiplier = isTouchDevice ? 1.4 : 1.0; // Reduced from 1.5 for better consistency
  
  // Enhanced edge point handling - more aggressive for problematic cases
  const edgeMultiplier = isEdgePoint ? 1.5 : 1.0; // Increased from 1.3
  
  // Canvas density adjustment - smaller canvases need proportionally larger hit areas
  const densityFactor = Math.max(0.8, Math.min(1.3, 800 / resInfo.density));
  
  // Calculate final radius with all factors
  let dynamicRadius = baseRadius * scaleFactor * touchMultiplier * edgeMultiplier * densityFactor;
  
  // Adaptive bounds based on canvas size - no fixed limits
  const minRadius = Math.max(
    isTouchDevice ? 10 : 6,
    Math.ceil(Math.min(canvasSize.width, canvasSize.height) * 0.008) // 0.8% of smaller dimension
  );
  
  const maxRadius = Math.min(
    40, // Absolute maximum
    Math.floor(Math.min(canvasSize.width, canvasSize.height) * 0.05) // 5% of smaller dimension
  );
  
  dynamicRadius = Math.max(minRadius, Math.min(maxRadius, dynamicRadius));
  
  return Math.round(dynamicRadius);
};

const MAX_POINTS = {           // strict – cannot add more
  people      : 4,
  queue       : 4,
  idle_area   : 4,
  vehicle     : 2,
  waiting_time: 12, // Support up to 3 zones (4 points each)
};

const MAX_ZONES_PER_MODE = {
  waiting_time: 3, // Maximum number of zones for waiting time tracker
};

const POINTS_PER_ZONE = {
  waiting_time: { min: 3, max: 12, default: 4 }, // Flexible polygon: 3-12 points
};

/* ────────────── component ────────────────────────────────────────── */
export default function LineCalibrationCanvas({
  streamUrl,
  onSaveComplete,
  processingWidth,
  processingHeight,
  modeSlug,
  sourcePath,
}) {
  const canvasRef                 = useRef(null);
  const imageRef                  = useRef(null);
  const dragOffset                = useRef({ x: 0, y: 0 });

  const [clicks, setClicks]       = useState([]);           // {x,y}
  const [dragIndex, setDragIndex] = useState(null);
  const [isDragging, setDragging] = useState(false);
  
  // Multi-zone state for waiting_time mode
  const [zones, setZones] = useState([]);                   // Array of zone objects: [{id, points: [], completed: bool, isPolygon: bool}]
  const [currentZoneIndex, setCurrentZoneIndex] = useState(0);
  const [hoverIndex, setHoverIndex] = useState(null);       // Index of point being hovered over
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 }); // Actual canvas dimensions

  const [saving,  setSaving]      = useState(false);
  const [message, setMessage]     = useState("");
  const [error,   setError]       = useState(null);
  
  // Resolution tracking and warnings
  const [resolutionWarning, setResolutionWarning] = useState(null);
  const [savedCalibrationMeta, setSavedCalibrationMeta] = useState(null);

  const [imgDims, setImgDims]     = useState({ width: 0, height: 0 });
  const [cacheUrl, setCacheUrl]   = useState(null);
  
  // Touch and mobile-specific state
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [touchStartTime, setTouchStartTime] = useState(0);
  const [lastTouchPos, setLastTouchPos] = useState(null);
  
  // Note: Polygon drawing state removed as it's not currently used but reserved for future enhancements

  /* ───────── function definitions ───────── */
  // Enhanced canvas size synchronization with image-based sizing
  const updateCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) {
      console.warn('Canvas or image ref not available for size update');
      return false;
    }
    
    // Get the actual displayed image dimensions
    const imageRect = image.getBoundingClientRect();
    const newWidth = Math.round(imageRect.width);
    const newHeight = Math.round(imageRect.height);
    
    // Validate image dimensions
    if (!newWidth || !newHeight || newWidth <= 0 || newHeight <= 0) {
      console.warn('Invalid image dimensions detected:', { width: newWidth, height: newHeight });
      return false;
    }
    
    // Sanity check for reasonable canvas sizes
    const maxDimension = 8000; // Maximum for camera streams
    const minDimension = 100;  // Minimum for usability
    
    if (newWidth > maxDimension || newHeight > maxDimension) {
      console.warn('Image dimensions exceed maximum safe size:', { width: newWidth, height: newHeight });
      return false;
    }
    
    if (newWidth < minDimension || newHeight < minDimension) {
      console.warn('Image dimensions below minimum usable size:', { width: newWidth, height: newHeight });
      return false;
    }
    
    // Update canvas internal dimensions to match displayed image
    if (canvas.width !== newWidth || canvas.height !== newHeight) {
      canvas.width = newWidth;
      canvas.height = newHeight;
    }
    
    // Update state only if dimensions have changed
    if (newWidth !== canvasSize.width || newHeight !== canvasSize.height) {
      const resInfo = getResolutionClass(newWidth, newHeight);
      setCanvasSize({ width: newWidth, height: newHeight });
      
      // Enhanced logging for canvas size changes
      console.log('Canvas synchronized with image:', { 
        imageDimensions: { width: newWidth, height: newHeight },
        canvasInternalDimensions: { width: canvas.width, height: canvas.height },
        previousCanvasSize: canvasSize,
        cameraMetrics: {
          resolutionClass: resInfo.class,
          aspectRatio: Math.round(resInfo.aspectRatio * 100) / 100,
          isUltrawide: resInfo.isUltrawide,
          isPortrait: resInfo.isPortrait,
          area: resInfo.area
        }
      });
      
      return true;
    }
    return false;
  }, [canvasSize.width, canvasSize.height]);

  // Enhanced debugging system for scaling and coordinate validation
  const debugScalingIssues = useCallback((action, data = {}) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) return;
    
    const imageRect = image.getBoundingClientRect();
    const canvasRect = canvas.getBoundingClientRect();
    
    const scalingInfo = {
      action,
      timestamp: Date.now(),
      imageDisplaySize: { width: Math.round(imageRect.width), height: Math.round(imageRect.height) },
      canvasInternalSize: { width: canvas.width, height: canvas.height },
      canvasDisplaySize: { width: Math.round(canvasRect.width), height: Math.round(canvasRect.height) },
      stateCanvasSize: canvasSize,
      stateImgDims: imgDims,
      scaling: {
        imageVsCanvas: {
          x: canvas.width / imageRect.width,
          y: canvas.height / imageRect.height
        },
        displayVsInternal: {
          x: canvasRect.width / canvas.width,
          y: canvasRect.height / canvas.height
        }
      },
      ...data
    };
    
    // Log scaling issues or edge cases
    const hasScalingIssue = (
      Math.abs(scalingInfo.scaling.imageVsCanvas.x - 1.0) > 0.1 ||
      Math.abs(scalingInfo.scaling.imageVsCanvas.y - 1.0) > 0.1 ||
      Math.abs(scalingInfo.scaling.displayVsInternal.x - 1.0) > 0.1 ||
      Math.abs(scalingInfo.scaling.displayVsInternal.y - 1.0) > 0.1
    );
    
    if (hasScalingIssue || data.forceLog) {
      console.log('Scaling Debug:', scalingInfo);
    }
    
    return scalingInfo;
  }, [canvasSize, imgDims]);
  
  // Simple debug helper for coordinate validation
  const debugCoordinateValidation = useCallback((action, point, details = {}) => {
    if (point.x <= 2 || point.x >= canvasSize.width - 3 || point.y <= 2 || point.y >= canvasSize.height - 3) {
      debugScalingIssues(action + '_edge', { 
        point: { x: Math.round(point.x), y: Math.round(point.y) },
        distanceFromEdges: {
          left: point.x,
          right: canvasSize.width - point.x,
          top: point.y,
          bottom: canvasSize.height - point.y
        },
        ...details
      });
    }
  }, [canvasSize.width, canvasSize.height, debugScalingIssues]);

  /* ───────── instruction helper ───────── */
  const getInstr = useCallback((n) => {
    switch (modeSlug) {
      case "people":
        return [
          "Click Red Line Start (R1).",
          "Click Red Line End (R2).",
          "Click Pink Line Start (P1).",
          "Click Pink Line End (P2). Drag to adjust.",
          "All 4 points set. Drag to adjust, Save, or Undo.",
        ][Math.min(n, 4)];
      case "vehicle":
        return [
          "Click Line Start (L1).",
          "Click Line End (L2). Drag to adjust.",
          "Both points set. Drag to adjust, Save, or Undo.",
        ][Math.min(n, 2)];
      case "queue":
      case "idle_area": {
        const name = modeSlug === "queue" ? "queue area" : "idle monitoring area";
        if (n < MAX_POINTS[modeSlug])
          return `Click point ${n + 1} to define the ${name}. Drag to adjust.`;
        return `All ${MAX_POINTS[modeSlug]} points selected. Drag, Save, or Undo.`;
      }
      case "waiting_time": {
        const currentZone = zones[currentZoneIndex];
        const currentZonePoints = currentZone ? currentZone.points.length : 0;
        const zoneNumber = currentZoneIndex + 1;
        const totalZones = zones.length;
        const minPoints = POINTS_PER_ZONE.waiting_time.min;
        const maxPoints = POINTS_PER_ZONE.waiting_time.max;
        
        if (currentZonePoints < minPoints) {
          const instructions = [
            `Click first corner of waiting zone ${zoneNumber}`,
            `Click second corner of waiting zone ${zoneNumber}`,
            `Click third corner to form the polygon area`
          ];
          return instructions[currentZonePoints] || `Click point ${currentZonePoints + 1} for zone ${zoneNumber}. Drag to adjust.`;
        } else if (currentZonePoints < maxPoints && (!currentZone?.completed)) {
          return `Zone ${zoneNumber}: ${currentZonePoints} points. Click to add more (max ${maxPoints}) or 'Complete Zone' to finish polygon.`;
        } else {
          if (totalZones < MAX_ZONES_PER_MODE.waiting_time) {
            return `Zone ${zoneNumber} complete! Click 'Add Zone' for another zone, or Save to finish.`;
          }
          return `All ${totalZones} waiting zones complete! You can drag points to adjust, then Save.`;
        }
      }
      default: return "Calibration";
    }
  }, [modeSlug, zones, currentZoneIndex]);

  /* ───────── effects ───────── */
  useEffect(() => { setMessage(getInstr(clicks.length)); },
    [clicks.length, getInstr]);
    
  // Debug scaling issues when canvas size changes
  useEffect(() => {
    if (canvasSize.width > 0 && canvasSize.height > 0) {
      debugScalingIssues('canvas_size_change', { 
        forceLog: false,
        reason: 'canvasSize state updated'
      });
    }
  }, [canvasSize.width, canvasSize.height, debugScalingIssues]);
  
  // Debug scaling issues when image dimensions change
  useEffect(() => {
    if (imgDims.width > 0 && imgDims.height > 0) {
      debugScalingIssues('image_dims_change', { 
        forceLog: false,
        reason: 'imgDims state updated'
      });
    }
  }, [imgDims.width, imgDims.height, debugScalingIssues]);

  useEffect(() => {
    if (streamUrl) {
      setCacheUrl(`${streamUrl}?_cb=${Date.now()}`);
      setClicks([]); setImgDims({ width: 0, height: 0 }); setError(null);
      
      // Load saved calibration metadata for resolution tracking
      const metadataKey = `calibration_meta_${modeSlug}_${btoa(sourcePath).replace(/[^a-zA-Z0-9]/g, '')}`;
      const savedMeta = localStorage.getItem(metadataKey);
      if (savedMeta) {
        try {
          setSavedCalibrationMeta(JSON.parse(savedMeta));
        } catch (e) {
          console.warn('Failed to parse saved calibration metadata:', e);
          setSavedCalibrationMeta(null);
        }
      } else {
        setSavedCalibrationMeta(null);
      }
      
      // Initialize zones for waiting_time mode
      if (modeSlug === "waiting_time") {
        const initialZone = { id: 1, points: [], completed: false, isPolygon: true };
        setZones([initialZone]);
        setCurrentZoneIndex(0);
      } else {
        setZones([]);
        setCurrentZoneIndex(0);
      }
    } else setCacheUrl(null);
  }, [streamUrl, modeSlug, sourcePath]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (imageRef.current) {
        const img = imageRef.current;
        const rect = img.getBoundingClientRect();
        setImgDims(prev => ({
          ...prev,
          width: rect.width,
          height: rect.height
        }));
        // Ensure canvas size is updated after resize
        setTimeout(() => updateCanvasSize(), 50);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateCanvasSize]);

  // Handle orientation change for mobile devices
  useEffect(() => {
    const handleOrientationChange = () => {
      // Small delay to ensure DOM updates after orientation change
      setTimeout(handleResize, 100);
    };

    const handleResize = () => {
      if (imageRef.current) {
        const img = imageRef.current;
        const rect = img.getBoundingClientRect();
        setImgDims(prev => ({
          ...prev,
          width: rect.width,
          height: rect.height
        }));
        // Ensure canvas size is updated after orientation change
        setTimeout(() => updateCanvasSize(), 150);
      }
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    return () => window.removeEventListener('orientationchange', handleOrientationChange);
  }, [updateCanvasSize]);

  // Detect touch device capability
  useEffect(() => {
    const checkTouchDevice = () => {
      const hasTouch = 'ontouchstart' in window || 
                      navigator.maxTouchPoints > 0 || 
                      navigator.msMaxTouchPoints > 0;
      setIsTouchDevice(hasTouch);
    };
    
    checkTouchDevice();
    
    // Listen for changes in touch capability (e.g., external mouse)
    window.addEventListener('touchstart', checkTouchDevice, { once: true });
    return () => window.removeEventListener('touchstart', checkTouchDevice);
  }, []);
  
  // Helper to get global point index from zone and point indices (for drag handling)
  const getGlobalPointIndex = useCallback((zoneIndex, pointIndex) => {
    let globalIndex = 0;
    for (let i = 0; i < zoneIndex; i++) {
      globalIndex += zones[i] ? zones[i].points.length : 0;
    }
    return globalIndex + pointIndex;
  }, [zones]);
  
  // Helper to get zone and point indices from global index
  const getZonePointIndices = useCallback((globalIndex) => {
    let currentIndex = 0;
    for (let zoneIndex = 0; zoneIndex < zones.length; zoneIndex++) {
      const zone = zones[zoneIndex];
      if (!zone) continue;
      if (globalIndex < currentIndex + zone.points.length) {
        return { zoneIndex, pointIndex: globalIndex - currentIndex };
      }
      currentIndex += zone.points.length;
    }
    return { zoneIndex: -1, pointIndex: -1 };
  }, [zones]);

  /* ───────── drawing ───────── */
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !imgDims.width) return;
    
    // Get current image display dimensions
    const imageRect = image.getBoundingClientRect();
    const displayWidth = Math.round(imageRect.width);
    const displayHeight = Math.round(imageRect.height);
    
    // Validate dimensions before updating
    if (displayWidth <= 0 || displayHeight <= 0) {
      console.warn('Invalid display dimensions in draw:', { displayWidth, displayHeight });
      return;
    }
    
    // Update canvas internal dimensions to match image display
    if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
      debugScalingIssues('canvas_resize_in_draw', {
        forceLog: true,
        oldCanvasSize: { width: canvas.width, height: canvas.height },
        newCanvasSize: { width: displayWidth, height: displayHeight },
        reason: 'Canvas size mismatch with image display size'
      });
      
      canvas.width = displayWidth;
      canvas.height = displayHeight;
      // Update canvas size state
      updateCanvasSize();
    }
    
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2; 
    
    // Dynamic font size based on canvas size for better readability
    const baseFontSize = Math.max(10, Math.min(16, canvas.width / 80));
    ctx.font = `bold ${baseFontSize}px Arial`;
    
    // Add visual boundary indicators for better user guidance
    const showBoundaryGuides = isDragging;
    if (showBoundaryGuides) {
      ctx.strokeStyle = "rgba(100, 100, 100, 0.3)";
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      ctx.strokeRect(0, 0, canvas.width, canvas.height);
      ctx.setLineDash([]);
      ctx.lineWidth = 2;
    }

    // Draw points - handle waiting_time multi-zone differently
    if (modeSlug === "waiting_time" && zones.length > 0) {
      // Draw all completed zones first
      zones.forEach((zone, zoneIndex) => {
        const isActiveZone = zoneIndex === currentZoneIndex;
        const zoneColors = ["#28a745", "#007bff", "#dc3545"]; // Green, Blue, Red
        const zoneColor = zoneColors[zoneIndex % zoneColors.length];
        
        zone.points.forEach((p, pointIndex) => {
          const color = isActiveZone ? zoneColor : `${zoneColor}80`; // Semi-transparent for inactive zones
          const label = `Z${zoneIndex + 1}P${pointIndex + 1}`;
          
          // Draw point with better visibility
          ctx.beginPath(); ctx.arc(p.x, p.y, POINT_RADIUS + 1, 0, 2 * Math.PI);
          ctx.fillStyle = color; ctx.fill();
          
          // Add white border for better visibility
          ctx.strokeStyle = "white"; ctx.lineWidth = 2; ctx.stroke();
          
          // Highlight selected and hovered points
          const globalIndex = getGlobalPointIndex(zoneIndex, pointIndex);
          if (dragIndex === globalIndex) { 
            ctx.strokeStyle = "#ffd700"; // Gold color for selected/dragging
            ctx.lineWidth = 3; 
            ctx.stroke(); 
          } else if (hoverIndex === globalIndex) {
            ctx.strokeStyle = "#ff6b35"; // Orange color for hover
            ctx.lineWidth = 2.5; 
            ctx.stroke(); 
          }
          
          // Draw label with better contrast
          const labelX = p.x + 10;
          const labelY = p.y + 5;
          ctx.fillStyle = "rgba(0, 0, 0, 0.8)"; // Dark background for label
          ctx.fillRect(labelX - 2, labelY - 12, 25, 16);
          ctx.fillStyle = "white"; 
          ctx.strokeStyle = "black"; 
          ctx.lineWidth = 0.5;
          ctx.font = "bold 10px Arial";
          ctx.strokeText(label, labelX, labelY); 
          ctx.fillText(label, labelX, labelY);
        });
      });
    } else {
      // Original single-zone drawing for other modes
      clicks.forEach((p, i) => {
        let color = "cyan", label = `P${i + 1}`;
        if (modeSlug === "people")   { color = i < 2 ? "red" : "deeppink"; label = ["R1","R2","P1","P2"][i]; }
        if (modeSlug === "vehicle")     { color = "dodgerblue"; label = ["L1","L2"][i]; }
        if (modeSlug === "queue")       color = "orange";
        if (modeSlug === "idle_area")   color = "lightblue";

        // Draw point with better visibility
        ctx.beginPath(); ctx.arc(p.x, p.y, POINT_RADIUS + 1, 0, 2 * Math.PI);
        ctx.fillStyle = color; ctx.fill();
        
        // Add white border for better visibility
        ctx.strokeStyle = "white"; ctx.lineWidth = 2; ctx.stroke();
        
        // Highlight selected and hovered points
        if (dragIndex === i) { 
          ctx.strokeStyle = "#ffd700"; // Gold color for selected/dragging
          ctx.lineWidth = 3; 
          ctx.stroke(); 
        } else if (hoverIndex === i) {
          ctx.strokeStyle = "#ff6b35"; // Orange color for hover
          ctx.lineWidth = 2.5; 
          ctx.stroke(); 
        }
        
        // Draw label with better contrast
        const labelX = p.x + 10;
        const labelY = p.y + 5;
        ctx.fillStyle = "rgba(0, 0, 0, 0.8)"; // Dark background for label
        ctx.fillRect(labelX - 2, labelY - 12, 20, 16);
        ctx.fillStyle = "white"; 
        ctx.strokeStyle = "black"; 
        ctx.lineWidth = 0.5;
        ctx.font = "bold 11px Arial";
        ctx.strokeText(label, labelX, labelY); 
        ctx.fillText(label, labelX, labelY);
      });
    }

    ctx.lineWidth = 3;
    if (modeSlug === "people") {
      if (clicks.length >= 2) { ctx.strokeStyle="red"; ctx.beginPath(); ctx.moveTo(clicks[0].x, clicks[0].y); ctx.lineTo(clicks[1].x, clicks[1].y); ctx.stroke(); }
      if (clicks.length >= 4) { ctx.strokeStyle="deeppink"; ctx.beginPath(); ctx.moveTo(clicks[2].x, clicks[2].y); ctx.lineTo(clicks[3].x, clicks[3].y); ctx.stroke(); }
    }
    if (modeSlug === "vehicle" && clicks.length === 2) {
      ctx.strokeStyle = "dodgerblue"; ctx.beginPath();
      ctx.moveTo(clicks[0].x, clicks[0].y); ctx.lineTo(clicks[1].x, clicks[1].y); ctx.stroke();
    }
    // Draw zone shapes
    if (["queue","idle_area"].includes(modeSlug) && clicks.length >= 2) {
      let strokeColor, fillColor;
      if (modeSlug === "queue") {
        strokeColor = "orange";
        fillColor = "rgba(255, 165, 0, 0.1)";
      } else if (modeSlug === "idle_area") {
        strokeColor = "lightblue";
        fillColor = "rgba(173, 216, 230, 0.1)";
      }
      
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 3;
      ctx.beginPath(); 
      ctx.moveTo(clicks[0].x, clicks[0].y);
      clicks.slice(1).forEach(pt => ctx.lineTo(pt.x, pt.y));
      
      if (clicks.length >= 3) {
        ctx.closePath();
        // Fill the area with semi-transparent color
        ctx.fillStyle = fillColor;
        ctx.fill();
      }
      ctx.stroke();
    }
    
    // Draw waiting_time zones with multi-zone support
    if (modeSlug === "waiting_time" && zones.length > 0) {
      const zoneColors = [
        { stroke: "#28a745", fill: "rgba(40, 167, 69, 0.15)" }, // Green
        { stroke: "#007bff", fill: "rgba(0, 123, 255, 0.15)" }, // Blue  
        { stroke: "#dc3545", fill: "rgba(220, 53, 69, 0.15)" }   // Red
      ];
      
      zones.forEach((zone, zoneIndex) => {
        if (!zone || zone.points.length < 2) return;
        
        const isActiveZone = zoneIndex === currentZoneIndex;
        const colors = zoneColors[zoneIndex % zoneColors.length];
        const strokeColor = isActiveZone ? colors.stroke : `${colors.stroke}80`;
        const fillColor = isActiveZone ? colors.fill : colors.fill.replace('0.15', '0.08');
        
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = isActiveZone ? 3 : 2;
        ctx.beginPath(); 
        ctx.moveTo(zone.points[0].x, zone.points[0].y);
        zone.points.slice(1).forEach(pt => ctx.lineTo(pt.x, pt.y));
        
        if (zone.points.length >= 3) {
          ctx.closePath();
          // Fill the area with semi-transparent color
          ctx.fillStyle = fillColor;
          ctx.fill();
        }
        ctx.stroke();
        
        // Add zone completion indicator
        if (zone.completed) {
          ctx.fillStyle = isActiveZone ? "rgba(40, 167, 69, 0.9)" : "rgba(40, 167, 69, 0.6)";
          ctx.font = "bold 12px Arial";
          const text = `Zone ${zoneIndex + 1}`;
          const textWidth = ctx.measureText(text).width;
          const centerX = zone.points.reduce((sum, pt) => sum + pt.x, 0) / zone.points.length;
          const centerY = zone.points.reduce((sum, pt) => sum + pt.y, 0) / zone.points.length;
          
          // Text background
          ctx.fillStyle = "rgba(0, 0, 0, 0.8)";
          ctx.fillRect(centerX - textWidth/2 - 4, centerY - 10, textWidth + 8, 20);
          
          // Text
          ctx.fillStyle = "white";
          ctx.fillText(text, centerX - textWidth/2, centerY + 4);
        }
      });
    }
  }, [clicks, imgDims, modeSlug, dragIndex, zones, currentZoneIndex, hoverIndex, getGlobalPointIndex, isDragging, updateCanvasSize]);

  useEffect(draw, [draw]);

  // Ensure canvas size is updated when image dimensions change
  useEffect(() => {
    if (imgDims.width && imgDims.height) {
      // Small delay to ensure canvas has been updated before checking size
      const timeoutId = setTimeout(() => {
        updateCanvasSize();
      }, 10);
      return () => clearTimeout(timeoutId);
    }
  }, [imgDims.width, imgDims.height, updateCanvasSize]);
  
  // Check for resolution mismatch and show warnings
  useEffect(() => {
    if (savedCalibrationMeta && imgDims.width > 0 && imgDims.height > 0) {
      const currentAspectRatio = imgDims.width / imgDims.height;
      const savedAspectRatio = savedCalibrationMeta.original_image_width / savedCalibrationMeta.original_image_height;
      
      const widthMismatch = Math.abs(imgDims.width - savedCalibrationMeta.original_image_width) > 10;
      const heightMismatch = Math.abs(imgDims.height - savedCalibrationMeta.original_image_height) > 10;
      const aspectRatioMismatch = Math.abs(currentAspectRatio - savedAspectRatio) > 0.05;
      
      if (widthMismatch || heightMismatch || aspectRatioMismatch) {
        setResolutionWarning({
          type: 'resolution_mismatch',
          current: { width: imgDims.width, height: imgDims.height, aspectRatio: Math.round(currentAspectRatio * 100) / 100 },
          saved: { 
            width: savedCalibrationMeta.original_image_width, 
            height: savedCalibrationMeta.original_image_height, 
            aspectRatio: Math.round(savedAspectRatio * 100) / 100
          },
          processingDims: {
            width: savedCalibrationMeta.processing_width,
            height: savedCalibrationMeta.processing_height
          }
        });
      } else {
        setResolutionWarning(null);
      }
    }
  }, [imgDims.width, imgDims.height, savedCalibrationMeta]);

  /* ───────── helpers ───────── */

  // Enhanced coordinate calculation with improved scaling and edge handling
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image) {
      console.warn('Canvas or image ref not available for coordinate calculation');
      return { x: 0, y: 0, isValid: false };
    }
    
    // Get both canvas and image rectangles for accurate scaling
    const canvasRect = canvas.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();
    
    // Handle both mouse and touch events with validation
    const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
    const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
    
    // Use image rect for coordinate calculation as it represents the actual visible area
    const rect = imageRect;
    
    // Comprehensive validation of dimensions
    if (!canvas.width || !canvas.height || !rect.width || !rect.height) {
      console.warn('Invalid dimensions for coordinate calculation:', {
        canvasSize: { width: canvas.width, height: canvas.height },
        imageRect: { width: rect.width, height: rect.height },
        canvasRect: { width: canvasRect.width, height: canvasRect.height }
      });
      return { x: 0, y: 0, isValid: false };
    }
    
    // Validate client coordinates
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
      console.warn('Invalid client coordinates:', { clientX, clientY });
      return { x: 0, y: 0, isValid: false };
    }
    
    // Calculate position relative to the image (visible area)
    const relativeX = clientX - rect.left;
    const relativeY = clientY - rect.top;
    
    // Map to canvas coordinates - use canvas internal dimensions
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    // Validate scale factors
    if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || scaleX <= 0 || scaleY <= 0) {
      console.warn('Invalid scale factors:', { scaleX, scaleY, canvasSize: { width: canvas.width, height: canvas.height }, imageSize: { width: rect.width, height: rect.height }});
      return { x: 0, y: 0, isValid: false };
    }
    
    // Calculate final canvas coordinates
    const rawX = relativeX * scaleX;
    const rawY = relativeY * scaleY;
    
    // Validate raw coordinates
    if (!Number.isFinite(rawX) || !Number.isFinite(rawY)) {
      console.warn('Invalid raw coordinates calculated:', { rawX, rawY });
      return { x: 0, y: 0, isValid: false };
    }
    
    // Map to full canvas coordinate space - allow edge-to-edge positioning
    let x = Math.max(0, Math.min(canvas.width - 1, rawX)); // -1 to allow reaching true edge
    let y = Math.max(0, Math.min(canvas.height - 1, rawY)); // -1 to allow reaching true edge
    
    // Round to nearest pixel for precise positioning
    x = Math.round(x);
    y = Math.round(y);
    
    // Final validation with more permissive bounds
    if (!Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) {
      console.warn('Final coordinate validation failed:', { x, y, canvasSize: { width: canvas.width, height: canvas.height }});
      return { x: 0, y: 0, isValid: false };
    }
    
    return {
      x,
      y,
      isValid: true,
      // Debugging information
      rawX,
      rawY,
      relativeX,
      relativeY,
      scaleX,
      scaleY,
      imageRect: rect,
      canvasRect,
      // Edge detection for debugging
      isNearLeftEdge: x <= 5,
      isNearRightEdge: x >= canvas.width - 6,
      isNearTopEdge: y <= 5,
      isNearBottomEdge: y >= canvas.height - 6
    };
  };
  
  // Enhanced hover detection with consistent hit radius across all positions
  const checkHover = (pos) => {
    if (modeSlug === "waiting_time" && zones.length > 0) {
      let globalIndex = 0;
      for (let zoneIndex = 0; zoneIndex < zones.length; zoneIndex++) {
        const zone = zones[zoneIndex];
        if (!zone) continue;
        
        for (let pointIndex = 0; pointIndex < zone.points.length; pointIndex++) {
          const point = zone.points[pointIndex];
          
          // Use consistent radius for all points, regardless of position
          const hoverRadius = getDynamicRadius(canvasSize, BASE_HOVER_RADIUS, false);
          const d = Math.hypot(pos.x - point.x, pos.y - point.y);
          
          if (d < hoverRadius) {
            return globalIndex;
          }
          globalIndex++;
        }
      }
    } else {
      for (let i = 0; i < clicks.length; i++) {
        const point = clicks[i];
        
        // Use consistent radius for all points, regardless of position
        const hoverRadius = getDynamicRadius(canvasSize, BASE_HOVER_RADIUS, false);
        const d = Math.hypot(pos.x - point.x, pos.y - point.y);
        
        if (d < hoverRadius) {
          return i;
        }
      }
    }
    return null;
  };

  /* ───────── mouse / touch events ───────── */
  const onDown = (e) => {
    if (saving || !imgDims.width || error) return;
    const pos = getPos(e);
    
    // Validate coordinates before proceeding
    if (!pos.isValid) {
      console.warn('Invalid coordinates in onDown, skipping interaction');
      return;
    }
    
    // Handle waiting_time multi-zone mode
    if (modeSlug === "waiting_time") {
      // Check for hit on existing points first (for dragging)
      let globalIndex = 0;
      for (let zoneIndex = 0; zoneIndex < zones.length; zoneIndex++) {
        const zone = zones[zoneIndex];
        if (!zone) continue;
        
        for (let pointIndex = 0; pointIndex < zone.points.length; pointIndex++) {
          const point = zone.points[pointIndex];
          
          // Use consistent hit radius for all points
          const hitRadius = getDynamicRadius(canvasSize, BASE_HIT_RADIUS, false);
          const d = Math.hypot(pos.x - point.x, pos.y - point.y);
          
          if (d < hitRadius) {
            setDragIndex(globalIndex);
            setDragging(true);
            setCurrentZoneIndex(zoneIndex); // Switch to the zone being dragged
            dragOffset.current = { x: point.x - pos.x, y: point.y - pos.y };
            
            // Debug drag start for all points
            console.log('Drag started:', { 
              point: { x: Math.round(point.x), y: Math.round(point.y) },
              hitRadius, 
              distance: Math.round(d), 
              globalIndex,
              zoneIndex,
              pointIndex,
              canvasSize: { width: canvasSize.width, height: canvasSize.height }
            });
            
            return;
          }
          globalIndex++;
        }
      }
      
      // Add new point to current zone if not at max points and not completed
      const currentZone = zones[currentZoneIndex];
      const maxPoints = POINTS_PER_ZONE.waiting_time.max;
      
      if (currentZone && currentZone.points.length < maxPoints && !currentZone.completed) {
        setZones(prevZones => {
          const newZones = [...prevZones];
          newZones[currentZoneIndex] = {
            ...currentZone,
            points: [...currentZone.points, pos],
            // Don't auto-complete - let user decide when polygon is finished
            completed: false
          };
          return newZones;
        });
      } else if (currentZone?.completed) {
        setMessage(`Zone ${currentZoneIndex + 1} is completed. Add new zone or edit existing points.`);
      } else {
        setMessage(`Zone ${currentZoneIndex + 1} has reached maximum points (${maxPoints}). Complete zone or remove points.`);
      }
    } else {
      // Enhanced logic for other modes with consistent hit detection
      for (let i = clicks.length - 1; i >= 0; i--) {
        const point = clicks[i];
        
        // Use consistent hit radius for all points
        const hitRadius = getDynamicRadius(canvasSize, BASE_HIT_RADIUS, false);
        const d = Math.hypot(pos.x - point.x, pos.y - point.y);
        
        if (d < hitRadius) {
          setDragIndex(i); setDragging(true);
          dragOffset.current = { x: point.x - pos.x, y: point.y - pos.y };
          
          // Debug drag start for all points
          console.log('Drag started (non-waiting_time):', { 
            point: { x: Math.round(point.x), y: Math.round(point.y) },
            hitRadius, 
            distance: Math.round(d), 
            pointIndex: i,
            mode: modeSlug,
            canvasSize: { width: canvasSize.width, height: canvasSize.height }
          });
          
          return;
        }
      }
      if (clicks.length < MAX_POINTS[modeSlug]) setClicks(c => [...c, pos]);
      else setMessage(`Max ${MAX_POINTS[modeSlug]} points. Drag points or Save.`);
    }
  };

  const onMove = (e) => {
    const pos = getPos(e);
    
    // Validate coordinates before proceeding
    if (!pos.isValid) {
      console.warn('Invalid coordinates in onMove, skipping interaction');
      return;
    }
    
    if (isDragging) {
      // Enhanced dragging with true edge-to-edge support
      // Calculate new position with offset, allowing full canvas area
      const newX = pos.x + dragOffset.current.x;
      const newY = pos.y + dragOffset.current.y;
      
      // Allow positioning at true edges (0 to width-1, 0 to height-1)
      const x = Math.max(0, Math.min(newX, canvasSize.width - 1));
      const y = Math.max(0, Math.min(newY, canvasSize.height - 1));
      
      // Enhanced debug logging for drag movement
      if (x <= 1 || x >= canvasSize.width - 2 || y <= 1 || y >= canvasSize.height - 2) {
        console.log('Edge drag detected:', { 
          x, y, 
          canvasSize: { width: canvasSize.width, height: canvasSize.height },
          dragIndex,
          isAtEdge: {
            left: x <= 1,
            right: x >= canvasSize.width - 2,
            top: y <= 1, 
            bottom: y >= canvasSize.height - 2
          }
        });
      }
      
      if (modeSlug === "waiting_time" && zones.length > 0) {
        // Find which zone and point index this dragIndex corresponds to
        const { zoneIndex, pointIndex } = getZonePointIndices(dragIndex);
        if (zoneIndex >= 0 && pointIndex >= 0) {
          setZones(prevZones => {
            const newZones = [...prevZones];
            const updatedPoints = [...newZones[zoneIndex].points];
            updatedPoints[pointIndex] = { x, y };
            newZones[zoneIndex] = { ...newZones[zoneIndex], points: updatedPoints };
            return newZones;
          });
        }
      } else {
        // Update points for other modes
        setClicks(c => c.map((pt, i) => (i === dragIndex ? { x, y } : pt)));
      }
    } else {
      // Handle hover detection when not dragging
      const hoveredIndex = checkHover(pos);
      setHoverIndex(hoveredIndex);
    }
  };

  const stopDrag = () => { setDragging(false); setDragIndex(null); };

  /* ───────── undo / save ───────── */
  const undoLast = () => {
    if (saving) return;
    
    if (modeSlug === "waiting_time" && zones.length > 0) {
      const currentZone = zones[currentZoneIndex];
      if (currentZone && currentZone.points.length > 0) {
        // Remove last point from current zone
        setZones(prevZones => {
          const newZones = [...prevZones];
          const updatedPoints = newZones[currentZoneIndex].points.slice(0, -1);
          newZones[currentZoneIndex] = {
            ...newZones[currentZoneIndex],
            points: updatedPoints,
            completed: false
          };
          return newZones;
        });
      }
    } else {
      setClicks(c => c.slice(0, -1));
    }
  };
  
  // Zone management functions for waiting_time mode
  const addZone = () => {
    if (zones.length < MAX_ZONES_PER_MODE.waiting_time) {
      const newZoneId = Math.max(...zones.map(z => z.id), 0) + 1;
      const newZone = { id: newZoneId, points: [], completed: false, isPolygon: true };
      setZones(prevZones => [...prevZones, newZone]);
      setCurrentZoneIndex(zones.length); // Switch to the new zone
    }
  };
  
  const deleteCurrentZone = () => {
    if (zones.length > 1) {
      setZones(prevZones => prevZones.filter((_, index) => index !== currentZoneIndex));
      setCurrentZoneIndex(Math.max(0, currentZoneIndex - 1));
    }
  };
  
  const switchToZone = (zoneIndex) => {
    if (zoneIndex >= 0 && zoneIndex < zones.length) {
      setCurrentZoneIndex(zoneIndex);
    }
  };

  // Complete current polygon zone
  const completeCurrentZone = () => {
    const currentZone = zones[currentZoneIndex];
    const minPoints = POINTS_PER_ZONE.waiting_time.min;
    
    if (currentZone && currentZone.points.length >= minPoints) {
      setZones(prevZones => {
        const newZones = [...prevZones];
        newZones[currentZoneIndex] = {
          ...currentZone,
          completed: true
        };
        return newZones;
      });
      setMessage(`Zone ${currentZoneIndex + 1} completed with ${currentZone.points.length} points!`);
    } else {
      setError(`Zone needs at least ${minPoints} points to complete.`);
    }
  };

  // Remove last point from current zone
  const removeLastPoint = () => {
    const currentZone = zones[currentZoneIndex];
    if (currentZone && currentZone.points.length > 0) {
      setZones(prevZones => {
        const newZones = [...prevZones];
        const updatedPoints = newZones[currentZoneIndex].points.slice(0, -1);
        newZones[currentZoneIndex] = {
          ...newZones[currentZoneIndex],
          points: updatedPoints,
          completed: false // Un-complete if removing points
        };
        return newZones;
      });
    }
  };

  const save = async () => {
    if (!sourcePath || !imgDims.width || processingWidth <= 0) {
      setError("Missing data for saving."); return;
    }
    
    // Validation for waiting_time multi-zone mode
    if (modeSlug === "waiting_time") {
      if (zones.length === 0) {
        setError("At least one zone is required."); return;
      }
      
      // Check that each zone has the minimum required points and is completed
      const minPoints = POINTS_PER_ZONE.waiting_time.min;
      for (let i = 0; i < zones.length; i++) {
        const zone = zones[i];
        if (!zone.completed) {
          setError(`Zone ${i + 1} is not completed. Use 'Complete Zone' button first.`); 
          return;
        }
        if (zone.points.length < minPoints) {
          setError(`Zone ${i + 1} needs at least ${minPoints} points, has ${zone.points.length}.`); 
          return;
        }
      }
    } else {
      // Original validation for other modes
      const reqPts = MAX_POINTS[modeSlug];
      if (clicks.length !== reqPts) { setError(`Need ${reqPts} points.`); return; }
    }
    
    setSaving(true); setMessage("Saving…"); setError(null);

    // Enhanced coordinate validation and bounds checking
    const validateAndScaleCoordinates = (points, mode) => {
      const scaledPoints = points.map(p => {
        // Scale coordinates to processing dimensions
        let x = Math.round((p.x / imgDims.width) * processingWidth);
        let y = Math.round((p.y / imgDims.height) * processingHeight);
        
        // Bounds checking - ensure coordinates are within processing dimensions
        x = Math.max(0, Math.min(x, processingWidth - 1));
        y = Math.max(0, Math.min(y, processingHeight - 1));
        
        return { x, y };
      });
      
      // Log coordinate validation info
      console.log(`Coordinate validation for ${mode}:`, {
        originalCanvasSize: { width: imgDims.width, height: imgDims.height },
        processingSize: { width: processingWidth, height: processingHeight },
        pointCount: points.length,
        scaledPoints: scaledPoints.slice(0, 2) // Log first 2 points as sample
      });
      
      return scaledPoints;
    };
    
    let payload;
    switch (modeSlug) {
      case "people": {
        const scaled = validateAndScaleCoordinates(clicks, "people");
        const [r1,r2,p1,p2] = scaled;
        payload = { red:[[r1.x,r1.y],[r2.x,r2.y]], pink:[[p1.x,p1.y],[p2.x,p2.y]] };
        break;
      }
      case "vehicle": {
        const scaled = validateAndScaleCoordinates(clicks, "vehicle");
        const [l1,l2] = scaled;
        payload = { line:[[l1.x,l1.y],[l2.x,l2.y]] };
        break;
      }
      case "queue":
      case "idle_area": {
        const scaled = validateAndScaleCoordinates(clicks, modeSlug);
        payload = scaled.map(pt => [pt.x, pt.y]);
        break;
      }
      case "waiting_time": {
        // Multi-zone payload with enhanced validation
        payload = zones.map((zone, zoneIndex) => {
          const scaledZonePoints = validateAndScaleCoordinates(zone.points, `waiting_time_zone_${zoneIndex + 1}`);
          return scaledZonePoints.map(pt => [pt.x, pt.y]);
        });
        break;
      }
      default: setError("Unsupported mode"); setSaving(false); return;
    }

    try {
      // Store metadata locally for resolution tracking (for future backend integration)
      const calibrationMetadata = {
        original_canvas_width: canvasSize.width,
        original_canvas_height: canvasSize.height,
        original_image_width: imgDims.width,
        original_image_height: imgDims.height,
        processing_width: processingWidth,
        processing_height: processingHeight,
        calibration_timestamp: new Date().toISOString(),
        mode: modeSlug,
        source_path: sourcePath
      };
      
      // Save metadata to localStorage for resolution mismatch detection
      const metadataKey = `calibration_meta_${modeSlug}_${btoa(sourcePath).replace(/[^a-zA-Z0-9]/g, '')}`;
      localStorage.setItem(metadataKey, JSON.stringify(calibrationMetadata));
      
      // Send coordinates in current backend format (backward compatible)
      await saveLineCalibration(modeSlug, sourcePath, payload);
      setMessage("Saved!"); setTimeout(onSaveComplete, 1200);
    } catch (err) {
      const errorMsg = err.detail || err.message || "API error";
      if (modeSlug === "waiting_time" && errorMsg.includes("polygon")) {
        setError("Invalid waiting area zone. Please ensure you have 4 corner points forming a closed area.");
      } else if (modeSlug === "waiting_time") {
        setError(`Waiting area calibration failed: ${errorMsg}`);
      } else {
        setError(`Save error: ${errorMsg}`);
      }
      setSaving(false);
    }
  };

  /* ───────── UI helpers ───────── */
  const canSave = (() => {
    if (!imgDims.width) return false;
    
    if (modeSlug === "waiting_time") {
      // For waiting_time, check that we have at least one completed zone with minimum points
      const minPoints = POINTS_PER_ZONE.waiting_time.min;
      return zones.length > 0 && zones.every(zone => 
        zone.completed && zone.points.length >= minPoints
      );
    }
    
    return clicks.length === MAX_POINTS[modeSlug];
  })();
  
  const title = (() => {
    if (modeSlug === "waiting_time") {
      const completedZones = zones.filter(zone => zone.completed).length;
      return `Waiting Time Tracker (${completedZones}/${zones.length} zones complete)`;
    }
    
    return ({
      people      : "Footfall Tracker Lines (4 pts)",
      queue       : "Queue Monitor Area (4 pts)",
      idle_area   : "Idle Tracker Area (4 pts)",
      vehicle     : "Vehicle Counter Line (2 pts)",
    })[modeSlug] || "Calibration";
  })();
  
  // Count current points for display
  const getCurrentPointCount = () => {
    if (modeSlug === "waiting_time") {
      const currentZone = zones[currentZoneIndex];
      return currentZone ? currentZone.points.length : 0;
    }
    return clicks.length;
  };
  
  const getTotalPointCount = () => {
    if (modeSlug === "waiting_time") {
      return zones.reduce((total, zone) => total + zone.points.length, 0);
    }
    return clicks.length;
  };

  /* ───────── render ───────── */
  return (
    <div className="calibration-container" style={{
      // Enhanced mobile support
      touchAction: "manipulation",
      userSelect: "none",
      WebkitUserSelect: "none",
      maxWidth: "100%",
      overflow: "hidden"
    }}>
      <h2 style={{ 
        fontSize: isTouchDevice ? "1.4rem" : "1.5rem",
        marginBottom: "1rem" 
      }}>
        Calibrate: {title}
      </h2>
      <p className={`message ${saving ? "saving" : error ? "error-message" : ""}`}>
        {error || message || " "}
      </p>
      
      {/* Resolution mismatch warning */}
      {resolutionWarning && (
        <div className="resolution-warning" style={{
          backgroundColor: "#fff3cd",
          border: "1px solid #ffeaa7",
          borderRadius: "4px",
          padding: "12px",
          margin: "10px 0",
          fontSize: "0.9em"
        }}>
          <div style={{ fontWeight: "600", color: "#856404", marginBottom: "8px" }}>
            ⚠️ Resolution Mismatch Detected
          </div>
          <div style={{ color: "#856404", lineHeight: "1.4" }}>
            <strong>Current:</strong> {resolutionWarning.current.width}×{resolutionWarning.current.height} (AR: {resolutionWarning.current.aspectRatio})<br/>
            <strong>Saved:</strong> {resolutionWarning.saved.width}×{resolutionWarning.saved.height} (AR: {resolutionWarning.saved.aspectRatio})<br/>
            <small style={{ marginTop: "4px", display: "block" }}>
              Calibration lines may not align properly. Consider re-calibrating for accurate results.
            </small>
          </div>
        </div>
      )}
      {isTouchDevice && (
        <p style={{ 
          fontSize: "0.9em", 
          color: "#2196F3", 
          fontWeight: "500",
          marginBottom: "0.5rem",
          padding: "0.5rem",
          backgroundColor: "#E3F2FD",
          borderRadius: "4px",
          border: "1px solid #BBDEFB"
        }}>
          📱 Touch and drag to position zone points. Tap to add new points.
        </p>
      )}
      <p style={{ fontSize: "0.8em", color: "#aaa", wordBreak: "break-all" }}>
        Source: {sourcePath}
      </p>

      <div className="calibration-stream-wrapper" style={{ 
        position: "relative", 
        width: "100%", 
        maxWidth: "100vw",
        height: "fit-content",
        display: "flex",
        justifyContent: "center",
        alignItems: "center"
      }}>
        {cacheUrl ? (
          <img
            ref={imageRef}
            src={cacheUrl}
            alt="Calibration Stream"
            onLoad={e => {
              const img = e.target; 
              // Wait for image to be fully rendered before getting dimensions
              setTimeout(() => {
                const rect = img.getBoundingClientRect();
                setImgDims({ 
                  width: rect.width, 
                  height: rect.height,
                  naturalWidth: img.naturalWidth,
                  naturalHeight: img.naturalHeight
                });
                // Force canvas size update after image loads
                setTimeout(() => updateCanvasSize(), 10);
              }, 50);
            }}
            onError={() => setError("Failed to load stream")}
            style={{ 
              display: "block", 
              maxWidth: "100%",
              maxHeight: "80vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              cursor: saving||!imgDims.width||error? "default" : "crosshair"
            }}
            draggable="false"
          />
        ) : (
          <div className="no-stream-placeholder"
               style={{ minHeight:300, display:"flex", alignItems:"center", justifyContent:"center",
                        border:"1px dashed grey" }}>
            <p>{error || "Loading stream…"}</p>
          </div>
        )}

        {imgDims.width > 0 && !error && cacheUrl && (
          <canvas
            ref={canvasRef}
            onMouseDown={onDown} 
            onMouseMove={onMove}
            onMouseUp={stopDrag} 
            onMouseLeave={() => {
              stopDrag();
              setHoverIndex(null); // Clear hover state when mouse leaves canvas
            }}
            onTouchStart={e => {
              e.preventDefault(); // Prevent scrolling on touch
              e.stopPropagation(); // Prevent event bubbling
              setTouchStartTime(Date.now());
              const touch = e.touches[0];
              if (touch) {
                setLastTouchPos({ x: touch.clientX, y: touch.clientY });
                // Enhanced touch coordinate handling
                const touchPos = getPos(touch);
                if (touchPos.isValid) {
                  debugCoordinateValidation('touch_start', touchPos, { 
                    touchId: touch.identifier,
                    clientPos: { x: touch.clientX, y: touch.clientY }
                  });
                  onDown(touch);
                } else {
                  console.warn('Invalid touch coordinates on touch start');
                }
              }
            }}
            onTouchMove={e => {
              e.preventDefault();
              e.stopPropagation();
              const touch = e.touches[0];
              
              if (touch && lastTouchPos) {
                const deltaX = Math.abs(touch.clientX - lastTouchPos.x);
                const deltaY = Math.abs(touch.clientY - lastTouchPos.y);
                
                // Optimized movement threshold for precise edge positioning
                const movementThreshold = isDragging ? 0.5 : 2; // Very sensitive when dragging
                
                if (deltaX > movementThreshold || deltaY > movementThreshold || isDragging) {
                  setLastTouchPos({ x: touch.clientX, y: touch.clientY });
                  
                  // Enhanced touch move debugging
                  if (isDragging) {
                    const touchPos = getPos(touch);
                    if (touchPos.isValid) {
                      debugCoordinateValidation('touch_move', touchPos, { 
                        touchId: touch.identifier,
                        movement: { deltaX, deltaY },
                        threshold: movementThreshold
                      });
                      onMove(touch);
                    }
                  } else {
                    onMove(touch);
                  }
                }
              }
            }}
            onTouchEnd={e => {
              e.preventDefault();
              e.stopPropagation();
              
              const touchDuration = Date.now() - touchStartTime;
              
              // Enhanced touch end handling
              if (isDragging && lastTouchPos) {
                const finalPos = getPos({ 
                  clientX: lastTouchPos.x, 
                  clientY: lastTouchPos.y 
                });
                if (finalPos.isValid) {
                  debugCoordinateValidation('touch_end', finalPos, { 
                    touchDuration,
                    wasDragging: true
                  });
                } else {
                  console.warn('Invalid final touch position on touch end');
                }
              }
              
              // Handle long press for potential context actions
              if (touchDuration > 800) {
                console.log('Long press detected:', touchDuration, 'ms - could enable context menu');
              }
              
              setLastTouchPos(null);
              stopDrag();
            }}
            onTouchCancel={e => {
              e.preventDefault();
              
              // Enhanced touch cancel handling
              if (isDragging) {
                console.log('Touch drag cancelled - stopping drag operation');
              }
              
              // Clean up touch state
              setLastTouchPos(null);
              setTouchStartTime(0);
              stopDrag();
            }}
            style={{ 
              position: "absolute", 
              top: 0, 
              left: 0,
              width: "100%",
              height: "100%",
              cursor: saving||error? "default" : 
                      dragIndex!==null? "grabbing" :
                      hoverIndex!==null? "grab" : "crosshair",
              zIndex: 10,
              touchAction: "none",
              userSelect: "none",
              pointerEvents: "auto"
            }}
          />
        )}
      </div>

      <div className="calibration-controls">
        {modeSlug === "waiting_time" ? (
          <>
            {/* Zone Management Controls */}
            <div className="zone-management-controls" style={{ marginBottom: "1rem" }}>
              <div className="zone-tabs" style={{ 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "0.5rem", 
                marginBottom: "0.5rem",
                justifyContent: isTouchDevice ? "center" : "flex-start"
              }}>
                {zones.map((zone, index) => (
                  <button
                    key={zone.id}
                    onClick={() => switchToZone(index)}
                    disabled={saving}
                    className={`zone-tab ${index === currentZoneIndex ? 'active' : ''}`}
                    style={{
                      padding: isTouchDevice ? "0.6rem 1rem" : "0.4rem 0.8rem",
                      border: "2px solid",
                      borderColor: index === currentZoneIndex ? "#28a745" : "#ccc",
                      backgroundColor: index === currentZoneIndex ? "#28a745" : "white",
                      color: index === currentZoneIndex ? "white" : "#333",
                      borderRadius: "4px",
                      cursor: saving ? "not-allowed" : "pointer",
                      fontSize: isTouchDevice ? "1rem" : "0.9em",
                      minHeight: isTouchDevice ? "44px" : "auto", // Touch target size
                      minWidth: isTouchDevice ? "44px" : "auto"
                    }}
                  >
                    Zone {index + 1} ({zone.points.length}/{POINTS_PER_ZONE.waiting_time.max})
                    {zone.completed && " ✓"}
                  </button>
                ))}
              </div>
              
              <div className="zone-action-buttons" style={{ 
                display: "flex", 
                gap: "0.5rem", 
                flexWrap: "wrap",
                justifyContent: isTouchDevice ? "center" : "flex-start"
              }}>
                {/* Complete Zone Button */}
                {zones[currentZoneIndex] && !zones[currentZoneIndex].completed && zones[currentZoneIndex].points.length >= POINTS_PER_ZONE.waiting_time.min && (
                  <button
                    onClick={completeCurrentZone}
                    disabled={saving}
                    className="button-primary"
                    style={{ 
                      fontSize: isTouchDevice ? "1rem" : "0.9em",
                      minHeight: isTouchDevice ? "44px" : "auto",
                      backgroundColor: "#28a745",
                      borderColor: "#28a745"
                    }}
                  >
                    ✓ Complete Zone {currentZoneIndex + 1}
                  </button>
                )}
                
                {/* Remove Last Point Button */}
                {zones[currentZoneIndex] && zones[currentZoneIndex].points.length > 0 && !zones[currentZoneIndex].completed && (
                  <button
                    onClick={removeLastPoint}
                    disabled={saving}
                    className="button-secondary"
                    style={{ 
                      fontSize: isTouchDevice ? "1rem" : "0.9em",
                      minHeight: isTouchDevice ? "44px" : "auto",
                      backgroundColor: "#ffc107",
                      borderColor: "#ffc107",
                      color: "#212529"
                    }}
                  >
                    ← Remove Point
                  </button>
                )}
                
                <button
                  onClick={addZone}
                  disabled={zones.length >= MAX_ZONES_PER_MODE.waiting_time || saving}
                  className="button-secondary"
                  style={{ 
                    fontSize: isTouchDevice ? "1rem" : "0.9em",
                    minHeight: isTouchDevice ? "44px" : "auto"
                  }}
                >
                  + Add Zone ({zones.length}/{MAX_ZONES_PER_MODE.waiting_time})
                </button>
                
                {zones.length > 1 && (
                  <button
                    onClick={deleteCurrentZone}
                    disabled={saving}
                    className="button-secondary"
                    style={{ 
                      fontSize: isTouchDevice ? "1rem" : "0.9em",
                      minHeight: isTouchDevice ? "44px" : "auto",
                      backgroundColor: "#dc3545", 
                      borderColor: "#dc3545", 
                      color: "white" 
                    }}
                  >
                    Delete Zone {currentZoneIndex + 1}
                  </button>
                )}
              </div>
            </div>
            
            {/* Standard Controls */}
            <div className="standard-controls" style={{ 
              display: "flex", 
              gap: "1rem", 
              flexWrap: "wrap",
              justifyContent: isTouchDevice ? "center" : "flex-start"
            }}>
              <button 
                onClick={undoLast} 
                disabled={getTotalPointCount() === 0 || saving} 
                className="button-secondary"
                style={{ 
                  fontSize: isTouchDevice ? "1rem" : "0.9em",
                  minHeight: isTouchDevice ? "44px" : "auto"
                }}
              >
                Undo Last ({getCurrentPointCount()}/{zones[currentZoneIndex]?.points.length || 0} current)
              </button>
              <button 
                onClick={save} 
                disabled={!canSave || saving || !!error} 
                className="button-primary"
                style={{ 
                  fontSize: isTouchDevice ? "1rem" : "0.9em",
                  minHeight: isTouchDevice ? "44px" : "auto"
                }}
              >
                {saving ? "Saving…" : `Save All Zones (${zones.filter(z => z.completed).length} complete)`}
              </button>
            </div>
          </>
        ) : (
          /* Original controls for other modes */
          <>
            <button onClick={undoLast} disabled={!clicks.length || saving} className="button-secondary">
              Undo Last Point ({clicks.length})
            </button>
            <button onClick={save} disabled={!canSave || saving || !!error} className="button-primary">
              {saving ? "Saving…" : `Save Zone (${clicks.length}/${MAX_POINTS[modeSlug]} points)`}
            </button>
          </>
        )}
      </div>

      <div style={{ fontSize:"0.8em", marginTop:10, color:"#666" }}>
        <p>Points are automatically scaled to {processingWidth}×{processingHeight}.
          {modeSlug === "waiting_time" && ` Each zone is tracked independently for waiting time statistics.`}
        </p>
        {modeSlug === "waiting_time" && (
          <div style={{ marginTop: 8, padding: 8, backgroundColor: "#f8f9fa", borderRadius: 4, border: "1px solid #dee2e6" }}>
            <strong>🎯 Custom Polygon Zones:</strong>
            <ul style={{ margin: "8px 0", paddingLeft: "20px", fontSize: "0.9em" }}>
              <li><strong>Flexible shapes:</strong> Draw 3-12 points for any polygon shape (not just rectangles!)</li>
              <li><strong>Edge-to-edge:</strong> Drag points precisely to screen corners and edges</li>
              <li><strong>Complete zones:</strong> Click "Complete Zone" when your polygon is ready (minimum 3 points)</li>
              <li><strong>Edit freely:</strong> Add/remove points before completing, drag any point to adjust</li>
              <li><strong>Multiple zones:</strong> Create up to {MAX_ZONES_PER_MODE.waiting_time} custom polygon zones</li>
            </ul>
            <div style={{ fontSize: "0.8em", color: "#666", marginTop: "8px", padding: "4px 8px", backgroundColor: "#e8f5e8", borderRadius: "3px" }}>
              <strong>Current Zone {currentZoneIndex + 1}:</strong> {zones[currentZoneIndex]?.points.length || 0} points 
              {zones[currentZoneIndex]?.completed ? " ✅ COMPLETED" : ` (min ${POINTS_PER_ZONE.waiting_time.min}, max ${POINTS_PER_ZONE.waiting_time.max})`} | 
              <strong>Total:</strong> {zones.filter(z => z.completed).length}/{zones.length} zones complete
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────── prop-types ───────── */
LineCalibrationCanvas.propTypes = {
  streamUrl        : PropTypes.string,
  onSaveComplete   : PropTypes.func.isRequired,
  processingWidth  : PropTypes.number.isRequired,
  processingHeight : PropTypes.number.isRequired,
  modeSlug         : PropTypes.oneOf(["people","queue","idle_area","vehicle","waiting_time"]).isRequired,
  sourcePath       : PropTypes.string.isRequired,
};

LineCalibrationCanvas.defaultProps = {
  streamUrl: null,
};